/**
 * computeLaunchBlocker — the ONE rule for "is Start blocked, and why", shared by
 * every launch surface. Moved out of `LaunchForm.tsx` unchanged.
 *
 * Precedence: re-analysis, then the validator's reason, then the
 * missing-term-identity sentence, then the caller's own reason.
 *
 * Pure: no React.
 */

import type { PolicyInForce } from "~/common/workflows/types/client";
import {
  missingTermIdentitySources,
  resolveTermIdentityPlacement,
} from "~/common/workflows/launch/PolicyTermField";
import { REANALYSE_BLOCKS_START_REASON } from "~/common/workflows/launch/ReanalyseControl";
import {
  mergeLaunchSlice,
  splitLaunchValues,
  type LaunchBinding,
} from "~/common/workflows/launch/launchSchemaBinding";
import {
  findPolicyOption,
  readPolicyTermKey,
} from "~/common/workflows/launch/policyPrefillOptions";
import {
  validateLaunch,
  type LaunchValidation,
} from "~/common/workflows/launch/validateLaunch";

export interface LaunchBlockerArgs {
  binding: LaunchBinding;
  values: Record<string, unknown>;
  policyOptions: readonly PolicyInForce[];
  reanalysing?: boolean;
  extraReason?: string | null;
}

export interface LaunchBlocker {
  validation: LaunchValidation;
  missingSources: string[];
  blockingReason: string | null;
}

export function computeLaunchBlocker({
  binding,
  values,
  policyOptions,
  reanalysing = false,
  extraReason = null,
}: LaunchBlockerArgs): LaunchBlocker {
  const { runLevelValues, actions } = splitLaunchValues(binding, values);
  const validation = validateLaunch({
    binding,
    runLevelValues,
    actions,
    policyOptions,
  });

  /**
   * Declared term-identity keys the term control cannot honestly write. Surfaced
   * exactly like `missingRoles` AND blocking, because writing a partial identity
   * silently is the 2026-08-05 wrong-term defect.
   */
  const placement = resolveTermIdentityPlacement(binding);
  const seen = new Set<string>();
  for (const action of actions) {
    const flat = mergeLaunchSlice(binding, runLevelValues, action);
    const option = findPolicyOption(
      policyOptions,
      typeof flat.policy_number === "string" ? flat.policy_number : null,
      readPolicyTermKey(flat),
    );
    for (const key of missingTermIdentitySources(placement.keys, option)) {
      seen.add(key);
    }
  }
  const missingSources = [...seen];

  // Re-analysis FIRST: the values are about to be replaced, so launching now
  // runs on the very values the AM asked to get rid of (story 28873, TEST
  // ac7f759a), and every other reason may change once the fresh brief lands.
  const blockingReason =
    (reanalysing ? REANALYSE_BLOCKS_START_REASON : null) ??
    validation.blockingReason ??
    (missingSources.length > 0
      ? "The selected policy term does not supply every declared term-identity " +
        `field (${missingSources.join(", ")}). Launching would send a partial ` +
        "term identity, which is how a change lands on the wrong term."
      : null) ??
    extraReason;

  return { validation, missingSources, blockingReason };
}
